import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ==================== ITER63C-A — INTERCEPTEUR 403 EXPLICITE ====================
// Affiche un toast clair quand l'API renvoie 403, basé sur le contenu du detail.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const detail = (error.response.data?.detail || "").toLowerCase();
      let message = "Permission insuffisante";
      let description = null;
      if (detail.includes("révoqu")) {
        message = "Permission révoquée";
        description = "Cette permission vous a été retirée. Contactez l'architecte.";
      } else if (detail.includes("aucun accès")) {
        message = "Pas accès à ce chantier";
        description = "Vous n'êtes pas membre de ce chantier.";
      } else if (detail.includes("rôle") && detail.includes("non autorisé")) {
        description = "Cette action nécessite un rôle plus élevé. Contactez l'architecte du chantier.";
      } else if (detail) {
        description = error.response.data.detail;
      }
      // Pas de toast pour les /my-permissions (silencieux, géré par le hook) ; A6 (P2#8, #12) :
      // ni pour une requête marquée `silent403` (bloc facultatif selon le rôle, ex. « Ma semaine »)
      const url = error.config?.url || "";
      if (!url.includes("/my-permissions") && !error.config?.silent403) {
        toast.error(message, { description, duration: 6000 });
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTO-REFRESH JWT INTERCEPTOR ====================
// Au premier 401, tente automatiquement un refresh et rejoue la requête.
// Si le refresh échoue, on laisse l'UI gérer (checkAuth redirigera).
let _isRefreshing = false;
let _refreshQueue = [];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';

    // Ne pas refresh sur les endpoints d'auth eux-mêmes (évite boucles)
    const isAuthEndpoint = /\/api\/auth\/(login|refresh|logout)/.test(url);
    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (_isRefreshing) {
      // Une autre requête est en train de refresh — on attend
      return new Promise((resolve, reject) => {
        _refreshQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    _isRefreshing = true;
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      // Rejouer les requêtes en file d'attente
      _refreshQueue.forEach(({ resolve, config }) => resolve(axios(config)));
      _refreshQueue = [];
      return axios(originalRequest);
    } catch (refreshError) {
      _refreshQueue.forEach(({ reject }) => reject(refreshError));
      _refreshQueue = [];
      // Session morte — on laisse le contexte détecter (checkAuth redirigera)
      return Promise.reject(refreshError);
    } finally {
      _isRefreshing = false;
    }
  }
);

// Helper to format API errors
function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      // Try refresh token
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (refreshErr) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      const status = e.response?.status;
      const backendDetail = e.response?.data?.detail;
      // Le backend renvoie déjà des messages FR. On garde le detail si présent,
      // sinon fallback explicite par statut (429 rate-limit / 401 creds invalides).
      let errorMsg;
      if (backendDetail) {
        errorMsg = formatApiErrorDetail(backendDetail);
      } else if (status === 429) {
        errorMsg = "Trop de tentatives échouées. Réessayez dans quelques minutes ou utilisez « Mot de passe oublié ».";
      } else if (status === 401) {
        errorMsg = "Email ou mot de passe incorrect";
      } else {
        errorMsg = e.message || "Erreur de connexion. Réessayez.";
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setUser(false);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user && user !== false,
    isAdmin: user?.role === 'admin',
    isArchitect: user?.role === 'architect',
    isCollaborateur: user?.role === 'collaborateur', // RP32
    // RP32 — personnel du cabinet (créer un projet, annuaire…) ; ce qui engage le cabinet
    // dépend du rôle projet (/my-permissions).
    canEdit: ['admin', 'architect', 'collaborateur'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
